import type { RequestConfig } from "@app/config/axios/http";
import { divisionApi, divisionServices } from "@src/api/dictionaries/divisions";
import type { ApiDivision } from "@src/api/dictionaries/divisions";
import { measureApi } from "@src/api/dictionaries/measures";
import type { ApiMeasure } from "@src/api/dictionaries/measures";
import { usersApi, type ApiUserListItem } from "@src/api/users";
import { getOmpSearchString } from "@src/api/main/OMPTypes";
import { stockObjsApi, type ApiStockObj, type StockObj } from "@src/api/stock/stockObjs";
import { lotsServices, type ApiStockLot } from "../lots";
import { storePlacesServices, type ApiStorePlaceData, type StorePlaceRemainder } from "../storePlaces";
import { stockKanbanContainersApi } from "./api.core";
import {
    StockKanbanComplectState,
    type ApiStockKanbanContainerContentData,
    type ApiStockKanbanContainerData,
    type StockKanbanCompleteContainerParams,
    type StockKanbanContainer,
    type StockKanbanContainerContent,
    type StockKanbanContainerContentExtends,
    type StockKanbanContainerContentFilter,
    type StockKanbanContainerExtends,
    type StockKanbanContainerFilter,
    type StockKanbanCreateContainerByScanParams,
    type StockKanbanCreateContentParams,
    type StockKanbanMoveCompletedSelectionParams,
    type StockKanbanRemoveContentParams,
    type StockKanbanTakeForComplectParams,
    type StockKanbanUpdateContentParams,
} from "./types";

/** Сервисный слой — контейнеры КАНБАН и связанные партии комплектации. */
export const stockKanbanContainersServices = {
    ...stockKanbanContainersApi,

    /**
     * Загружает контейнеры КАНБАН и при необходимости догружает справочники.
     * @param params фильтр и include
     * @param config опции HTTP-запроса
     */
    load: async (
        params: {
            filter?: StockKanbanContainerFilter;
            include?: Array<keyof StockKanbanContainerExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockKanbanContainer[]> => {
        const { filter, include } = params;
        const containers = await stockKanbanContainersApi.load({ filter }, config);

        if (include == null) {
            return containers.map((container) => serializeContainer(container));
        }

        const includeStockObj = include.includes("StockObj");
        const includeMeasure = include.includes("Measure");
        const includeDivision = include.includes("Division");
        const includeSection = include.includes("Section");
        const includeComplectUser = include.includes("ComplectUser");
        const includeRemainderCells = include.includes("RemainderCells");

        const stockObjCodes = new Set<number>();
        const measureCodes = new Set<number>();
        const divisionCodes = new Set<number>();
        const sectionCodes = new Set<number>();
        const userCodes = new Set<number>();

        containers.forEach((container) => {
            if (includeStockObj || includeRemainderCells) addValidCode(stockObjCodes, container.StockobjCode);
            if (includeMeasure) addValidCode(measureCodes, container.MeasureCode);
            if (includeDivision) addValidCode(divisionCodes, container.DivisionCode);
            if (includeSection) addValidCode(sectionCodes, container.SectionCode);
            if (includeComplectUser) addValidCode(userCodes, container.ComplectUserCode);
        });

        const stockObjs = new Map<number, StockObj>();
        const measures = new Map<number, ApiMeasure>();
        const divisions = new Map<number, ApiDivision>();
        const sections = new Map<number, ApiDivision>();
        const users = new Map<number, ApiUserListItem>();
        const remainders = new Map<string, StorePlaceRemainder[]>();
        const promises: Promise<unknown>[] = [];

        if (stockObjCodes.size > 0 && includeStockObj) {
            promises.push(
                stockObjsApi.load({ flt: { Codes: [...stockObjCodes] } }, config).then((items) => {
                    items.forEach((item) => stockObjs.set(item.Code, serializeStockObj(item)));
                }),
            );
        }

        if (measureCodes.size > 0) {
            promises.push(
                measureApi.load({ flt: { Codes: [...measureCodes] } }, config).then((items) => {
                    items.forEach((item) => measures.set(item.Code, item));
                }),
            );
        }

        if (divisionCodes.size > 0) {
            promises.push(
                divisionApi.load({ flt: { Codes: [...divisionCodes] } }, config).then(({ items }) => {
                    items.forEach((item) => divisions.set(item.Code, item));
                }),
            );
        }

        if (sectionCodes.size > 0) {
            promises.push(
                divisionServices.loadBySectionCodes({ sectionCodes: [...sectionCodes] }, config).then((items) => {
                    items.forEach((item) => {
                        if (item.SectionCode !== 0) {
                            sections.set(item.SectionCode, item);
                        }
                    });
                }),
            );
        }

        if (userCodes.size > 0) {
            promises.push(
                usersApi.loadListByFilter({ Codes: [...userCodes] }, config).then((items) => {
                    items.forEach((item) => users.set(item.Code, item));
                }),
            );
        }

        if (includeRemainderCells) {
            promises.push(loadRemainderCells(containers, remainders, config));
        }

        await Promise.all(promises);

        return containers.map((container) =>
            serializeContainer(container, {
                StockObj: includeStockObj ? stockObjs.get(container.StockobjCode) : undefined,
                Measure: includeMeasure ? measures.get(container.MeasureCode) : undefined,
                Division: includeDivision ? divisions.get(container.DivisionCode) : undefined,
                Section: includeSection ? sections.get(container.SectionCode) : undefined,
                ComplectUser: includeComplectUser ? users.get(container.ComplectUserCode) : undefined,
                RemainderCells: includeRemainderCells
                    ? (remainders.get(getContainerRemainderKey(container)) ?? [])
                    : undefined,
            }),
        );
    },

    /**
     * Возвращает один контейнер КАНБАН по коду объекта системы.
     * @param params код и include
     * @param config опции HTTP-запроса
     */
    loadByCode: async (
        params: {
            code: number;
            include?: Array<keyof StockKanbanContainerExtends>;
        },
        config?: RequestConfig,
    ): Promise<StockKanbanContainer | null> => {
        const items = await stockKanbanContainersServices.load(
            { filter: { SoCodes: [params.code] }, include: params.include },
            config,
        );
        return items[0] ?? null;
    },

    /**
     * Находит контейнер по скану `SoNum`, при необходимости берёт на комплектование и возвращает модель. \
     * `takeForComplect` вызывается только если `ComplectState = 0` и комплектовщик ещё не назначен.
     * @param params скан и текущий фильтр списка
     * @param config опции HTTP-запроса
     */
    createByScan: async (
        params: StockKanbanCreateContainerByScanParams,
        config?: RequestConfig,
    ): Promise<StockKanbanContainer> => {
        const soNum = getOmpSearchString(params.barcode, { cs: "case_dependent" });
        const found = await stockKanbanContainersServices.load(
            {
                filter: {
                    DivisionCodes: params.filter.DivisionCodes,
                    SoNum: soNum,
                },
                include: ["StockObj", "Measure", "Division", "Section", "ComplectUser", "RemainderCells"],
            },
            config,
        );

        if (found.length === 0) {
            throw new Error(`Контейнер КАНБАН для скана "${params.barcode}" не найден.`);
        }

        if (found.length > 1) {
            throw new Error(`Для скана "${params.barcode}" найдено несколько контейнеров КАНБАН.`);
        }

        const container = found[0];

        if (stockKanbanContainersServices.canTakeForComplect(container)) {
            await stockKanbanContainersApi.takeForComplect({ code: container.SoCode }, config);
            const refreshed = await stockKanbanContainersServices.loadByCode(
                {
                    code: container.SoCode,
                    include: ["StockObj", "Measure", "Division", "Section", "ComplectUser", "RemainderCells"],
                },
                config,
            );
            if (refreshed == null) {
                throw new Error("Контейнер КАНБАН не найден после взятия на комплектование.");
            }
            return refreshed;
        }

        return container;
    },

    /**
     * Берёт контейнер на комплектование и возвращает обновлённую модель.
     * @param params код контейнера
     * @param config опции HTTP-запроса
     */
    takeForComplect: async (
        params: StockKanbanTakeForComplectParams,
        config?: RequestConfig,
    ): Promise<StockKanbanContainer> => {
        await stockKanbanContainersApi.takeForComplect({ code: params.containerCode }, config);
        const refreshed = await stockKanbanContainersServices.loadByCode(
            {
                code: params.containerCode,
                include: ["StockObj", "Measure", "Division", "Section", "ComplectUser", "RemainderCells"],
            },
            config,
        );
        if (refreshed == null) {
            throw new Error("Контейнер КАНБАН не найден после взятия на комплектование.");
        }
        return refreshed;
    },

    /** Партии комплектации контейнера. */
    content: {
        /**
         * Загружает партии, выбранные для контейнера КАНБАН.
         * @param params фильтр и include
         * @param config опции HTTP-запроса
         */
        load: async (
            params: {
                filter?: StockKanbanContainerContentFilter;
                include?: Array<keyof StockKanbanContainerContentExtends>;
                measure?: ApiMeasure;
            },
            config?: RequestConfig,
        ): Promise<StockKanbanContainerContent[]> => {
            const containerCodes = resolveContainerCodes(params.filter);
            if (containerCodes.length === 0) {
                return [];
            }

            const items = await stockKanbanContainersApi.getLots({ containerCodes }, config);
            const codes = params.filter?.Codes;
            const filtered = codes != null && codes.length > 0 ? items.filter((item) => codes.includes(item.Code)) : items;

            return stockKanbanContainersServices.enrichKanbanContents(filtered, params.include, params.measure, config);
        },

        /**
         * Добавляет партию в контейнер КАНБАН. \
         * После `addLot` перечитывает весь состав (`getLots`) — ответ мутации для sync cache.
         * @param params данные партии
         * @param config опции HTTP-запроса
         */
        create: async (
            params: StockKanbanCreateContentParams,
            config?: RequestConfig,
        ): Promise<StockKanbanContainerContent[]> => {
            await stockKanbanContainersApi.addLot(
                {
                    lot: {
                        Code: 0,
                        ContainerCode: params.containerCode,
                        LotCode: params.lotCode,
                        Qty: params.qty,
                        SpCode: params.spCode,
                    },
                },
                config,
            );

            return loadContentsAfterMutation(params.containerCode, params.lotCode, config);
        },

        /**
         * Обновляет количество партии через `removeLot` + `addLot` (отдельного update на backend нет). \
         * Возвращает полный актуальный состав контейнера для sync cache.
         * @param params данные партии
         * @param config опции HTTP-запроса
         */
        update: async (
            params: StockKanbanUpdateContentParams,
            config?: RequestConfig,
        ): Promise<StockKanbanContainerContent[]> => {
            await stockKanbanContainersApi.removeLot({ code: params.contentCode }, config);
            await stockKanbanContainersApi.addLot(
                {
                    lot: {
                        Code: 0,
                        ContainerCode: params.containerCode,
                        LotCode: params.lotCode,
                        Qty: params.qty,
                        SpCode: params.spCode,
                    },
                },
                config,
            );

            return loadContentsAfterMutation(params.containerCode, params.lotCode, config);
        },

        /**
         * Удаляет выбранную партию контейнера.
         * @param params код связи
         * @param config опции HTTP-запроса
         */
        remove: async (params: StockKanbanRemoveContentParams, config?: RequestConfig): Promise<void> => {
            await stockKanbanContainersApi.removeLot({ code: params.contentCode }, config);
        },
    },

    /**
     * Завершает комплектование контейнера (`finishComplect`).
     * @param params код контейнера со статусом «на комплектовании»
     * @param config опции HTTP-запроса
     */
    completeContainer: async (params: StockKanbanCompleteContainerParams, config?: RequestConfig): Promise<void> => {
        await stockKanbanContainersApi.finishComplect({ code: params.containerCode }, config);
    },

    /**
     * Перемещает скомплектованные контейнеры (`moveContainers`).
     * @param params коды контейнеров со статусом «комплектование завершено»
     * @param config опции HTTP-запроса
     */
    moveCompletedSelection: async (
        params: StockKanbanMoveCompletedSelectionParams,
        config?: RequestConfig,
    ): Promise<void> => {
        if (params.codes.length === 0) {
            throw new Error("В выборке нет контейнеров с завершённым комплектованием.");
        }
        // `stockInplantCodes` из ответа пока не используем на ТСД — достаточно факта успешного перемещения.
        await stockKanbanContainersApi.moveContainers({ codes: params.codes }, config);
    },

    /**
     * Можно ли взять контейнер на комплектование вручную/по скану. \
     * Доступно при `ComplectState = 0` и отсутствии комплектовщика.
     * @param container контейнер КАНБАН
     */
    canTakeForComplect: (container: Pick<ApiStockKanbanContainerData, "ComplectState" | "ComplectUserCode">): boolean => {
        return container.ComplectState === StockKanbanComplectState.Idle && !isValidCode(container.ComplectUserCode);
    },

    /**
     * Можно ли завершить комплектование по статусу. \
     * Доступно при `ComplectState = 1`. Владельца не проверяет — см. `canFinishKanbanComplect`.
     * @param container контейнер КАНБАН
     */
    canFinishComplect: (container: Pick<ApiStockKanbanContainerData, "ComplectState">): boolean => {
        return container.ComplectState === StockKanbanComplectState.InProgress;
    },

    /**
     * Текущий пользователь — комплектовщик контейнера.
     * @param container контейнер КАНБАН
     * @param currentUserCode код текущего пользователя сессии
     */
    isKanbanComplectOwner: (
        container: Pick<ApiStockKanbanContainerData, "ComplectUserCode">,
        currentUserCode: number | null | undefined,
    ): boolean => {
        return (
            currentUserCode != null &&
            isValidCode(currentUserCode) &&
            isValidCode(container.ComplectUserCode) &&
            container.ComplectUserCode === currentUserCode
        );
    },

    /**
     * Можно ли менять партии контейнера (create/update/remove/scan). \
     * Только статус «на комплектовании» и текущий пользователь = комплектовщик.
     * @param container контейнер КАНБАН
     * @param currentUserCode код текущего пользователя сессии
     */
    canManageKanbanContents: (
        container: Pick<ApiStockKanbanContainerData, "ComplectState" | "ComplectUserCode">,
        currentUserCode: number | null | undefined,
    ): boolean => {
        return (
            stockKanbanContainersServices.canFinishComplect(container) &&
            stockKanbanContainersServices.isKanbanComplectOwner(container, currentUserCode)
        );
    },

    /**
     * Можно ли завершить комплектование текущим пользователем. \
     * Статус «на комплектовании» и пользователь = комплектовщик.
     * @param container контейнер КАНБАН
     * @param currentUserCode код текущего пользователя сессии
     */
    canFinishKanbanComplect: (
        container: Pick<ApiStockKanbanContainerData, "ComplectState" | "ComplectUserCode">,
        currentUserCode: number | null | undefined,
    ): boolean => {
        return stockKanbanContainersServices.canManageKanbanContents(container, currentUserCode);
    },

    /** Собирает доменную модель партии контейнера из wire-данных и догруженных связей. */
    serializeKanbanContent: (
        apiContent: ApiStockKanbanContainerContentData,
        includeData?: Partial<StockKanbanContainerContentExtends>,
    ): StockKanbanContainerContent => {
        return {
            ...apiContent,
            Lot: includeData?.Lot,
            Measure: includeData?.Measure,
            StorePlace: includeData?.StorePlace,
        };
    },

    /**
     * Догружает связи партий контейнера (Lot / Measure / StorePlace) по include. \
     * `StorePlace` при `SpCode = 0` («на полу») — явный `null`.
     */
    enrichKanbanContents: async (
        contents: ApiStockKanbanContainerContentData[],
        include: Array<keyof StockKanbanContainerContentExtends> | undefined,
        measure: ApiMeasure | undefined,
        config?: RequestConfig,
    ): Promise<StockKanbanContainerContent[]> => {
        if (include == null) {
            return contents.map((content) => stockKanbanContainersServices.serializeKanbanContent(content));
        }

        const includeLot = include.includes("Lot");
        const includeMeasure = include.includes("Measure");
        const includeStorePlace = include.includes("StorePlace");
        const lotCodes = new Set<number>();
        const storePlaceCodes = new Set<number>();

        contents.forEach((content) => {
            if (includeLot) addValidCode(lotCodes, content.LotCode);
            if (includeStorePlace) addValidCode(storePlaceCodes, content.SpCode);
        });

        const lots = new Map<number, ApiStockLot>();
        const storePlaces = new Map<number, ApiStorePlaceData>();
        const promises: Promise<unknown>[] = [];

        if (lotCodes.size > 0) {
            promises.push(
                lotsServices.load({ flt: { Codes: [...lotCodes] } }, config).then((items) => {
                    items.forEach((item) => lots.set(item.LotCode, item));
                }),
            );
        }

        if (storePlaceCodes.size > 0) {
            promises.push(
                storePlacesServices.load({ filter: { Codes: [...storePlaceCodes] } }, config).then((items) => {
                    items.forEach((item) => storePlaces.set(item.Code, item));
                }),
            );
        }

        await Promise.all(promises);

        return contents.map((content) =>
            stockKanbanContainersServices.serializeKanbanContent(content, {
                Lot: includeLot ? lots.get(content.LotCode) : undefined,
                Measure: includeMeasure ? measure : undefined,
                StorePlace: includeStorePlace
                    ? isValidCode(content.SpCode)
                        ? (storePlaces.get(content.SpCode) ?? null)
                        : null
                    : undefined,
            }),
        );
    },
} as const;

/**
 * Перечитывает все партии контейнера после create/update. \
 * `addLot` не возвращает созданную строку, а `LotCode` не уникален в составе —
 * поэтому в cache нужно класть полный список, а не одну строку через `find`+upsert.
 */
async function loadContentsAfterMutation(
    containerCode: number,
    lotCode: number,
    config?: RequestConfig,
): Promise<StockKanbanContainerContent[]> {
    const items = await stockKanbanContainersServices.content.load(
        {
            filter: { ContainerCode: containerCode },
            include: ["Lot", "Measure", "StorePlace"],
        },
        config,
    );

    if (!items.some((item) => item.LotCode === lotCode)) {
        throw new Error("Партия контейнера КАНБАН не найдена после сохранения.");
    }

    return items;
}

/**
 * Картотека для подсказки «Ячейки остатка» контейнера КАНБАН. \
 * Wire `DepotcardType = 1` в `stock/storePlaces.loadRemaindersByFilter`.
 */
const KANBAN_REMAINDER_CELLS_DEPOTCARD_TYPE = 1;

async function loadRemainderCells(
    containers: ApiStockKanbanContainerData[],
    target: Map<string, StorePlaceRemainder[]>,
    config?: RequestConfig,
): Promise<void> {
    const stockObjCodes = new Set<number>();

    containers.forEach((container) => {
        if (isValidCode(container.StockobjCode)) {
            stockObjCodes.add(container.StockobjCode);
        }
    });

    /**
     * Один запрос на ТМЦ: в `ApiStorePlaceRemainderData` нет `StockobjCode`, \
     * поэтому batch по нескольким кодам нельзя надёжно разложить по контейнерам. \
     * Фильтр: `DepotcardType` + `StockobjCodes`, без `DivisionCode`.
     */
    const remaindersByStockObj = new Map<number, StorePlaceRemainder[]>();

    await Promise.all(
        [...stockObjCodes].map(async (stockObjCode) => {
            const remainders = await storePlacesServices.remainders.load(
                {
                    filter: {
                        DepotcardType: KANBAN_REMAINDER_CELLS_DEPOTCARD_TYPE,
                        StockobjCodes: [stockObjCode],
                        OnlyPositiveRemainders: true,
                        InContextOfLot: false,
                    },
                    include: ["StorePlace"],
                },
                config,
            );
            remaindersByStockObj.set(stockObjCode, remainders);
        }),
    );

    containers.forEach((container) => {
        const key = getContainerRemainderKey(container);
        target.set(key, remaindersByStockObj.get(container.StockobjCode) ?? []);
    });
}

function resolveContainerCodes(filter: StockKanbanContainerContentFilter | undefined): number[] {
    if (filter?.ContainerCodes != null && filter.ContainerCodes.length > 0) {
        return filter.ContainerCodes;
    }
    if (filter?.ContainerCode != null && isValidCode(filter.ContainerCode)) {
        return [filter.ContainerCode];
    }
    return [];
}

function serializeContainer(
    apiContainer: ApiStockKanbanContainerData,
    includeData?: Partial<StockKanbanContainerExtends>,
): StockKanbanContainer {
    return {
        ...apiContainer,
        StockObj: includeData?.StockObj,
        Measure: includeData?.Measure,
        Division: includeData?.Division,
        Section: includeData?.Section,
        ComplectUser: includeData?.ComplectUser,
        RemainderCells: includeData?.RemainderCells,
    };
}

function serializeStockObj(apiStockObj: ApiStockObj): StockObj {
    return {
        ...apiStockObj,
        KoTypeObj: undefined,
    };
}

function getContainerRemainderKey(container: ApiStockKanbanContainerData): string {
    return String(container.StockobjCode);
}

function addValidCode(target: Set<number>, code: number): void {
    if (isValidCode(code)) {
        target.add(code);
    }
}

function isValidCode(code: number): boolean {
    return code !== 0;
}
